import { Component, ElementRef, Injector, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FcPhoneDto, FcPhoneTypeListItemDto } from 'webcommon/shared';
import { FcItemModalContext } from '../../../models';
import { FcItemListBaseComponent, FcListValidationService, FormControlExtended } from '../../generic';

export interface PhoneNumberFormGroup {
  phoneNumber?: FormControl<string>;
  phoneType?: FormControl<string>;
  phoneTypeUid?: FormControl<string>;
}

@Component({
  providers: [FcListValidationService],
  selector: 'fc-phone-number-list',
  templateUrl: './fc-phone-number-list.component.html',
})
export class FcPhoneNumberListComponent extends FcItemListBaseComponent implements OnChanges {
  @Input() baseId: string;
  @Input() form: FormGroup;
  @Input() listItem?: FcPhoneDto;
  @Input() isSubmitted = false;
  @Input() itemContext: FcItemModalContext;
  @Input() index = 1;
  @Input() specialCharacters = '';
  @ViewChild('phoneNumber', { static: true }) phoneNumberInput: ElementRef<HTMLInputElement>;

  public phoneTypeRequired = false;
  public isPhoneTypeDisabled = false;

  public selectedPks: string[] = [];

  public phoneNumberControl = new FormControl<string>('');
  public phoneTypeControl = new FormControlExtended<string>('');
  public phoneTypeUidControl = new FormControl<string>('');

  constructor(injector: Injector) {
    super(injector);

    this.phoneNumberControl
      .valueChanges
      .pipe(
        distinctUntilChanged(),
        this.takeUntilDestroy(),
        debounceTime(10)
      ).subscribe((phoneNumber: string) => {
        // If Phone Number is not empty and valid add required validator to Phone Type
        this.moveCursorWhenMaskChanged(phoneNumber);
        const validators = this.fcListValidation.getValidators(`Phone${this.index}.PhoneType`);
        if (typeof phoneNumber === 'string' && phoneNumber.length > 0) {
          if (this.phoneNumberControl.valid) {
            this.phoneTypeRequired = true;
            validators.push(Validators.required);
            this.phoneTypeControl.showErrorFlag = true;
          }
          this.isPhoneTypeDisabled = !this.phoneNumberControl.valid;
        } else {
          this.phoneTypeRequired = false;
          this.selectedPks = [];
          this.isPhoneTypeDisabled = true;
        }

        this.phoneTypeControl.setValidators(validators);
        this.phoneTypeControl.updateValueAndValidity();
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.itemContext != null) {
      this.fcListValidation.initValidation(this.itemContext, {
        [`Phone${this.index}.PhoneNumber`]: {
          RegExp: '^(([0-9]{3}-[0-9]{4})|((\\([0-9]{3}\\)[0-9]{3}-[0-9]{4})( x[0-9]{1,7})?))$',
        },
        [`Phone${this.index}.PhoneType`]: {
          MaxLength: 50,
        },
      });
    }
    if (changes.form != null) {
      this.addControlsToForm();
    }
    if (changes.itemContext != null || changes.form != null || changes.listItem != null) {
      this.setForm();
    }
  }

  public onPhoneTypeDataPopulate(phoneTypeListItems: FcPhoneTypeListItemDto[]): void {
    const [phoneTypeListItem] = phoneTypeListItems;
    if (phoneTypeListItem != null && typeof phoneTypeListItem.Name === 'string' && typeof phoneTypeListItem.PhoneTypeUid === 'string') {
      this.phoneTypeControl.setValue(phoneTypeListItem.Name);
      this.phoneTypeUidControl.setValue(phoneTypeListItem.PhoneTypeUid);
    } else {
      this.phoneTypeControl.setValue('');
      this.phoneTypeUidControl.setValue('');
    }
  }

  private addControlsToForm(): void {
    this.form.addControl('phoneNumber', this.phoneNumberControl);
    this.form.addControl('phoneType', this.phoneTypeControl);
    this.form.addControl('phoneTypeUid', this.phoneTypeUidControl);
  }

  public formatPhoneNumber(phoneNumberValue?: string): string {
    const regex = /^(\d{3}-\d{4})|(\(\d{3}\)\d{3}-\d{4})|(\(\d{3}\)\d{3}-\d{4} x\d{1,7})$/;
    if (typeof phoneNumberValue === 'string' && !regex.test(phoneNumberValue)) {
      switch (true) {
        case phoneNumberValue.length === 7:
          return phoneNumberValue?.replace(/(\d{3})(\d{4})/, '$1-$2');
        case phoneNumberValue.length === 10:
          return phoneNumberValue?.replace(/(\d{3})(\d{3})(\d{4})/, '($1)$2-$3');
        case phoneNumberValue.length > 10 && phoneNumberValue.length < 18:
          return phoneNumberValue?.replace(/^(\d{3})(\d{3})(\d{4})(\d{0,7})$/, '($1)$2-$3 x$4');
      }
    }
    return phoneNumberValue ?? '';
  }

  private setForm(): void {
    // Phone Number Control
    const formattedPhoneNumber = this.formatPhoneNumber(this.listItem?.PhoneNumber ?? '');
    this.phoneNumberControl.setValidators(this.fcListValidation.getValidators(`Phone${this.index}.PhoneNumber`));
    this.phoneNumberControl.setValue(formattedPhoneNumber);

    // Phone Type Control
    this.phoneTypeControl.setValidators(this.fcListValidation.getValidators(`Phone${this.index}.PhoneType`));
    this.phoneTypeControl.setValue(this.listItem?.PhoneType ?? '');

    // Phone Type Uid Control
    this.phoneTypeUidControl.setValue(this.listItem?.PhoneTypeUid ?? '');

    if (typeof this.listItem?.PhoneTypeUid === 'string') {
      this.selectedPks = [this.listItem.PhoneTypeUid];
    } else {
      this.selectedPks = [];
    }
  }

  private moveCursorWhenMaskChanged(value?: string): void {
    // We need to move cursor to tne end because of changing mask
    if (typeof value === 'string' && value.length === 11) {
      this.phoneNumberInput.nativeElement.setSelectionRange(value.length + 3, value.length + 3);
    }
  }
}
